import { useState } from 'react'
import { ExternalLink, ImageOff } from 'lucide-react'
import { TimelinePost } from '@/types/post';

interface PostMediaProps {
  post: TimelinePost
}

export function PostMedia({ post }: PostMediaProps) {
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set())
  const [embedError, setEmbedError] = useState(false)

  const postUrl = post.properties?.url || post.metadata?.url

  const rawImages = post.properties?.images || post.metadata?.media || []
  const images = Array.isArray(rawImages) ? rawImages : []
  
  const isVideoThumbnail = (url: string) => {
    return url.includes('video_thumb') || url.includes('amplify_video_thumb') || url.includes('ext_tw_video_thumb')
  }
  
  const imageUrls = images.filter(url => !isVideoThumbnail(url))
  const hasImages = imageUrls.length > 0

  const entitiesMedia = post.metadata?.entities?.media || post.metadata?.extendedEntities?.media || []
  const videoMedia = Array.isArray(entitiesMedia) 
    ? entitiesMedia.find((m: any) => m?.type === 'video')
    : null

  const hasVideo = !!videoMedia
  const videoThumbnail = videoMedia?.media_url_https || null
  const videoEmbedUrl = post.metadata?.embed_url || post.properties?.embed_url || null

  const mediaType = post.metadata?.media_type || post.properties?.media_type
  const mediaUrl = post.metadata?.media_url || post.properties?.media_url
  const embedUrl = post.metadata?.embed_url || post.properties?.embed_url
  const thumbnailUrl = post.metadata?.thumbnail_url || post.properties?.thumbnail_url

  const hasExplicitMediaType = mediaType && mediaType !== 'none'
  const hasMedia = hasExplicitMediaType || hasImages || hasVideo || (mediaUrl || thumbnailUrl || embedUrl)

  if (!hasMedia) {
    return null
  }

  const handleImageError = (index: number) => {
    setImageErrors(prev => new Set(prev).add(index))
  }

  if (hasVideo || mediaType === 'video') {
    const embed = embedUrl || videoEmbedUrl
    const videoThumbnailFromImages = images.find(url => isVideoThumbnail(url))
    const thumbnail = videoThumbnail || videoThumbnailFromImages || thumbnailUrl || mediaUrl

    if (embed && !embedError) {
      return (
        <div className="w-full rounded-lg overflow-hidden bg-gray-100 aspect-video relative group">
          <iframe
            src={embed}
            className="w-full h-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={post.title || 'Video embed'}
          />
          {postUrl && (
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center pointer-events-none">
              <a
                href={postUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg text-sm font-medium text-gray-900 hover:bg-gray-50 pointer-events-auto"
                onClick={() => setEmbedError(true)}
              >
                Ver no post original
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          )}
        </div>
      )
    }

    if (thumbnail && !imageErrors.has(0)) {
      return (
        <div className="w-full rounded-lg overflow-hidden bg-gray-100 relative group">
          <img
            src={thumbnail}
            alt={post.title || 'Video thumbnail'}
            className="w-full h-auto object-cover"
            onError={() => handleImageError(0)}
          />
          {postUrl && (
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center">
              <a
                href={postUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg text-sm font-medium text-gray-900 hover:bg-gray-50"
              >
                Ver no post original
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          )}
        </div>
      )
    }

    return (
      <div className="w-full bg-gray-100 rounded-lg p-4 flex items-center justify-center min-h-[200px]">
        <div className="text-center">
          <ImageOff className="w-12 h-12 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Vídeo indisponível</p>
          <p className="text-xs text-gray-400 mt-1">Perfil privado ou restrição da plataforma</p>
          {postUrl && (
            <a
              href={postUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mt-2"
            >
              Abrir na fonte
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    )
  }

  if (hasImages) {
    const validImages = imageUrls.filter((_, index) => !imageErrors.has(index))
    
    if (validImages.length === 0) {
      return (
        <div className="w-full bg-gray-100 rounded-lg p-4 flex items-center justify-center min-h-[200px]">
          <div className="text-center">
            <ImageOff className="w-12 h-12 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Mídia indisponível</p>
            <p className="text-xs text-gray-400 mt-1">Perfil privado ou restrição da plataforma</p>
            {postUrl && (
              <a
                href={postUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mt-2"
              >
                Abrir na fonte
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      )
    }

    if (validImages.length === 1) {
      return (
        <div className="w-full rounded-lg overflow-hidden bg-gray-100 relative group">
          <img
            src={validImages[0]}
            alt={post.title || 'Post image'}
            className="w-full h-auto object-cover"
            onError={() => handleImageError(0)}
          />
          {postUrl && (
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center">
              <a
                href={postUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg text-sm font-medium text-gray-900 hover:bg-gray-50"
              >
                Ver no post original
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          )}
        </div>
      )
    }

    return (
      <div className="w-full rounded-lg overflow-hidden bg-gray-100 grid grid-cols-2 gap-1 relative group">
        {validImages.slice(0, 4).map((imageUrl, index) => (
          <div key={index} className="relative aspect-square">
            <img
              src={imageUrl}
              alt={`${post.title || 'Post'} image ${index + 1}`}
              className="w-full h-full object-cover"
              onError={() => handleImageError(index)}
            />
            {index === 3 && validImages.length > 4 && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <span className="text-white font-semibold">+{validImages.length - 4}</span>
              </div>
            )}
          </div>
        ))}
        {postUrl && (
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center pointer-events-none">
            <a
              href={postUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg text-sm font-medium text-gray-900 hover:bg-gray-50 pointer-events-auto"
            >
              Ver no post original
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>
    )
  }

  if (mediaType === 'carousel' && thumbnailUrl) {
    return (
      <div className="w-full rounded-lg overflow-hidden bg-gray-100 relative group">
        <img
          src={thumbnailUrl}
          alt={post.title || 'Post carousel'}
          className="w-full h-auto object-cover"
          onError={() => handleImageError(0)}
        />
        {postUrl && (
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center">
            <a
              href={postUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg text-sm font-medium text-gray-900 hover:bg-gray-50"
            >
              Ver no post original
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>
    )
  }

  if (mediaUrl && !hasImages && !hasVideo) {
    return (
      <div className="w-full rounded-lg overflow-hidden bg-gray-100 relative group">
        <img
          src={mediaUrl}
          alt={post.title || 'Post media'}
          className="w-full h-auto object-cover"
          onError={() => handleImageError(0)}
        />
        {postUrl && (
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center">
            <a
              href={postUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg text-sm font-medium text-gray-900 hover:bg-gray-50"
            >
              Ver no post original
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>
    )
  }

  return null
}
